import { useState } from 'react'
import { Search, Plus, Edit2, Trash2, FileText, Loader2, ChevronLeft, ChevronRight, X, CheckCircle, Sparkles } from 'lucide-react'
import { SupabaseClient } from '@supabase/supabase-js'
import { useBlogPosts, useBlogPostMutations, BlogPost } from '@/lib/data'
import { BlogContentEditor } from './BlogContentEditor'

interface BlogPostsViewProps {
  client: SupabaseClient | null
}

const CATEGORY_OPTIONS = [
  { value: 'Investigation', label: 'Investigation', color: 'bg-blue-500/10 text-blue-400' },
  { value: 'Brand Spotlight', label: 'Brand Spotlight', color: 'bg-purple-500/10 text-purple-400' },
  { value: 'News', label: 'News', color: 'bg-emerald-500/10 text-emerald-400' },
  { value: 'Guide', label: 'Guide', color: 'bg-amber-500/10 text-amber-400' },
  { value: 'Opinion', label: 'Opinion', color: 'bg-rose-500/10 text-rose-400' },
]

export function BlogPostsView({ client }: BlogPostsViewProps) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [publishedFilter, setPublishedFilter] = useState<'all' | 'published' | 'draft'>('all')
  const [showModal, setShowModal] = useState(false)
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<BlogPost | null>(null)
  const limit = 20

  const { posts, total, isLoading, error, refetch } = useBlogPosts(client, {
    page,
    limit,
    search,
    category: categoryFilter || undefined,
    isPublished: publishedFilter === 'all' ? undefined : publishedFilter === 'published',
  })

  const { createPost, updatePost, deletePost, isLoading: mutationLoading, error: mutationError } = useBlogPostMutations(client)

  const totalPages = Math.ceil(total / limit)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  const handleSave = async (formData: Partial<BlogPost>) => {
    let result
    if (editingPost) {
      result = await updatePost(editingPost.id, formData)
    } else {
      result = await createPost(formData)
    }

    if (result) {
      setShowModal(false)
      setEditingPost(null)
      refetch()
    }
  }

  const handleDelete = async () => {
    if (deleteConfirm) {
      await deletePost(deleteConfirm.id)
      setDeleteConfirm(null)
      refetch()
    }
  }

  const getCategoryStyle = (category: string) => {
    return CATEGORY_OPTIONS.find(c => c.value === category)?.color || 'bg-[var(--bg-element)] text-[var(--text-muted)]'
  }

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
        <FileText size={48} className="mb-4 opacity-20" />
        <p className="text-lg font-medium">Not Connected</p>
        <p className="text-sm">Configure Supabase to view blog posts</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-[var(--text-main)]">Blog Posts</h2>
        <button
          onClick={() => {
            setEditingPost(null)
            setShowModal(true)
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90"
        >
          <Plus size={16} /> Add Post
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <form onSubmit={handleSearch} className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)]" size={18} />
          <input
            type="text"
            placeholder="Search posts..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
          />
        </form>

        <select
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value)
            setPage(1)
          }}
          className="bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
        >
          <option value="">All Categories</option>
          {CATEGORY_OPTIONS.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>

        <select
          value={publishedFilter}
          onChange={(e) => {
            setPublishedFilter(e.target.value as typeof publishedFilter)
            setPage(1)
          }}
          className="bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
        >
          <option value="all">All Posts</option>
          <option value="published">Published</option>
          <option value="draft">Drafts</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-muted)]">
            No blog posts found
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)]">
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Title</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Category</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Author</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Published</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Status</th>
                <th className="text-right p-4 text-[var(--text-muted)] font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-element)]">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {post.image_url ? (
                        <img
                          src={post.image_url}
                          alt={post.title}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-[var(--bg-element)] flex items-center justify-center">
                          <FileText size={16} className="text-[var(--text-muted)]" />
                        </div>
                      )}
                      <div>
                        <span className="text-[var(--text-main)] font-medium block">{post.title}</span>
                        <span className="text-[var(--text-muted)] text-xs">/{post.slug}</span>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${getCategoryStyle(post.category)}`}>
                      {post.category}
                    </span>
                  </td>
                  <td className="p-4 text-[var(--text-muted)]">
                    {post.author || '-'}
                  </td>
                  <td className="p-4 text-[var(--text-muted)]">
                    {post.published_at ? new Date(post.published_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="p-4">
                    {post.is_published ? (
                      <span className="text-emerald-400 text-sm flex items-center gap-1">
                        <CheckCircle size={14} /> Published
                      </span>
                    ) : (
                      <span className="text-[var(--text-muted)] text-sm">Draft</span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditingPost(post)
                          setShowModal(true)
                        }}
                        className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)] rounded-lg"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(post)}
                        className="p-2 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[var(--text-muted)] text-sm">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} posts
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg disabled:opacity-50"
            >
              <ChevronLeft size={16} className="text-[var(--text-muted)]" />
            </button>
            <span className="text-[var(--text-main)] px-4">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg disabled:opacity-50"
            >
              <ChevronRight size={16} className="text-[var(--text-muted)]" />
            </button>
          </div>
        </div>
      )}

      {/* Edit/Create Panel */}
      {showModal && (
        <BlogPostModal
          post={editingPost}
          onClose={() => {
            setShowModal(false)
            setEditingPost(null)
          }}
          onSave={handleSave}
          isLoading={mutationLoading}
          error={mutationError}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-[var(--text-main)] mb-2">Delete Blog Post</h3>
            <p className="text-[var(--text-muted)] mb-6">
              Are you sure you want to delete "{deleteConfirm.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-[var(--text-muted)] hover:text-[var(--text-main)]"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={mutationLoading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
              >
                {mutationLoading && <Loader2 size={16} className="animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface BlogPostModalProps {
  post: BlogPost | null
  onClose: () => void
  onSave: (data: Partial<BlogPost>) => void
  isLoading: boolean
  error: string | null
}

function BlogPostModal({ post, onClose, onSave, isLoading, error }: BlogPostModalProps) {
  const [formData, setFormData] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    category: post?.category || 'Investigation',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    image_url: post?.image_url || '',
    author: post?.author || 'Buy\'r Team',
    is_published: post?.is_published || false,
  })

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const submitData: Partial<BlogPost> = {
      title: formData.title,
      slug: formData.slug,
      category: formData.category,
      excerpt: formData.excerpt,
      content: formData.content,
      image_url: formData.image_url || '',
      author: formData.author,
      is_published: formData.is_published,
    }

    // Set published_at if publishing for the first time
    if (formData.is_published && !post?.is_published) {
      submitData.published_at = new Date().toISOString()
    }

    onSave(submitData)
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-out Panel */}
      <div className="fixed inset-y-0 right-0 w-[70%] max-w-4xl bg-[var(--bg-card)] border-l border-[var(--border)] z-50 shadow-2xl animate-slide-in-right flex flex-col">
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between shrink-0">
          <h2 className="text-xl font-bold text-[var(--text-main)]">
            {post ? 'Edit Blog Post' : 'Add Blog Post'}
          </h2>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-main)] p-2 hover:bg-[var(--bg-element)] rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Error Display */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
              <p className="font-medium">Error saving blog post</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
              placeholder="e.g., Who Owns Your Water?"
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Slug *</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                required
                placeholder="e.g., who-owns-your-water"
                className="flex-1 bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
              />
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, slug: generateSlug(prev.title) }))}
                className="px-3 py-2 bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text-main)] rounded-lg flex items-center gap-2"
                title="Generate from title"
              >
                <Sparkles size={16} />
              </button>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Category *</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              required
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
            >
              {CATEGORY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Author */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Author</label>
            <input
              type="text"
              value={formData.author}
              onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
              placeholder="Buy'r Team"
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
            />
          </div>

          {/* Image URL */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Image URL</label>
            <input
              type="text"
              value={formData.image_url}
              onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
              placeholder="https://..."
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
            />
            {formData.image_url && (
              <div className="mt-2">
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="w-32 h-20 object-cover rounded-lg"
                  onError={(e) => (e.currentTarget.style.display = 'none')}
                />
              </div>
            )}
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Excerpt *</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
              required
              rows={2}
              placeholder="A brief summary of the blog post..."
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)] resize-none"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Content *</label>
            <BlogContentEditor
              content={formData.content}
              onChange={(content) => setFormData(prev => ({ ...prev, content }))}
            />
          </div>

          {/* Published Toggle */}
          <div className="flex items-center gap-3 p-4 bg-[var(--bg-element)] rounded-lg">
            <input
              type="checkbox"
              id="is_published"
              checked={formData.is_published}
              onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
              className="w-5 h-5 rounded border-[var(--border)] text-[var(--primary)] focus:ring-[var(--primary)]"
            />
            <label htmlFor="is_published" className="text-[var(--text-main)] font-medium">
              Publish this post
            </label>
            <span className="text-[var(--text-muted)] text-sm">
              {formData.is_published ? 'Visible on the blog' : 'Saved as draft'}
            </span>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-[var(--border)]">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[var(--text-muted)] hover:text-[var(--text-main)]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.title || !formData.slug || !formData.excerpt || !formData.content}
              className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading && <Loader2 size={16} className="animate-spin" />}
              {post ? 'Update Post' : 'Create Post'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}
